import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Audience,
  AutomationActionStatus,
  AutomationStatus,
  IAutomation,
  IAutomationDenormalized,
  IExperience,
  IExperienceMessageDenormalized,
  IExperienceTemplate,
  ITask,
  ITaskDenormalized,
  MessageInstanceStatus,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  SubTaskStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField, IOrganization } from '@dadohr/shared/domainTypes/org';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { compact, every, keyBy, mapValues, orderBy } from 'lodash';
import { ActorDescriptor } from '../../features/Experiences/components/Actors';
import {
  getAutomationAudiencesFromTemplateById,
  getMessageAudiencesFromTemplateById,
  getTaskAudiencesFromTemplateById
} from '../experiences/audience';
import {
  collectReplacements,
  DenormalizationResources,
  toDenormalizedAutomationsWithInterpolations,
  toDenormalizedMessages,
  toDenormalizedTasks
} from '../experiences/instance';
import {
  createNotStartedMessageStatus,
  createNotStartedTask,
  createUpcomingAutomationInstance,
  getActorSortKey,
  getAllMessageTemplates,
  getMessageTemplateById,
  getTaskTemplateById
} from '../experiences/template';
import { getPresumedStartPositionEvaluatingTriggerChains } from '../experiences/timing';
import { updateDataInDoc } from '../firebase';
import { toDenormalizedOrg } from '../orgs';
import { InterpolationDefaultOptions } from '../set/interpolation';
import { addToTs, tsFromMillis } from '../time';

export type ItemData =
  | {
      type: 'TASK';
      actor: ActorDescriptor;
      ccActorKeys: string[];
      audience: Audience | null;
      customizedTiming: boolean;
      channels: ScheduleChannel[];
      data: ITaskDenormalized;
      triggeredBy?: ITaskDenormalized[];
    }
  | {
      type: 'AUTOMATION';
      audience: Audience | null;
      customizedTiming: boolean;
      data: IAutomationDenormalized;
      triggeredBy?: ITaskDenormalized[];
    }
  | {
      type: 'MESSAGE';
      actor: ActorDescriptor;
      ccActorKeys: string[];
      audience: Audience | null;
      customizedTiming: boolean;
      channels: ScheduleChannel[];
      data: IExperienceMessageDenormalized;
      triggeredBy?: ITaskDenormalized[];
    };

const ifTasksAndAllAreSkipped = (tasks: ITask[]) => {
  return !!(
    tasks.length && every(tasks, (tsk) => tsk.status === TaskStatus.SKIPPED)
  );
};

/** Derived from `_mapFn`'s own return type rather than hand-duplicated, so it can't drift. */
export type ParticipantDialogViewModel = NonNullable<ReturnType<typeof _mapFn>>;

export const _mapFn = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  participant: Doc<IUser> | null,
  resources: DenormalizationResources,
  actors: { [actorKey: string]: Doc<IUser> },
  customUserFields: CustomUserField[],
  org: IOrganization,
  actorDescriptors: { [actorKey: string]: ActorDescriptor },
  interpolationOptions: InterpolationDefaultOptions
) => {
  if (!participant) {
    return null;
  }

  const denormalizedOrg = toDenormalizedOrg(
    org,
    tpl.data.overrides?.org || null
  );
  // TODO
  // Denormalize ALL tasks, not only the participant
  // Sort tasks and automations with each other,
  // indicate what type they are for the appropriate rendering
  const replacements = collectReplacements(
    tpl,
    d,
    denormalizedOrg.name,
    actors,
    customUserFields,
    d.data.anchorDate.at
  );

  const tsks = Object.fromEntries(d.data.tasks.map((x) => [x.templateId, x]));
  const msgs = { ...d.data.messagesStatuses };
  const auts = { ...d.data.automations };

  const untriggeredItems: {
    msgs: {
      [tplId: string]: {
        msg: MessageInstanceStatus;
        triggeredBy: string[];
      };
    };
    tasks: {
      [tplId: string]: {
        task: ITask;
        triggeredBy: string[];
      };
    };
    auts: { [autId: string]: { aut: IAutomation; triggeredBy: string[] } };
  } = { msgs: {}, tasks: {}, auts: {} };

  orderBy(tpl.data.tasks, (t) => {
    // We need to make sure that items are sorted in (at least an approximate) execution order
    // The key is that in triggered item chains of A -> B -> C, evaluation of the code below
    // needs to happen in this order. If it would be evaluated in A -> C -> B then C would
    // look for a reference to B which doesn't exist yet.
    // See discussion under https://dadohr.slack.com/archives/C03AZ4M658T/p1707817574552639
    if (t.timing.type === 'fixed') {
      return t.timing.start;
    } else if (t.timing.type === 'dataChange') {
      return t.timing.earliestStart ?? 0;
    } else {
      return (
        getPresumedStartPositionEvaluatingTriggerChains(
          t.timing.dependentOn.map((x) => x.itemId),
          tpl.data.tasks
        ) + t.timing.end.duration
      );
    }
  }).forEach((t) => {
    if (t.timing.type === 'fixed') {
      return;
    }
    if (t.timing.type === 'dataChange') {
      return;
    }
    if (tsks[t.id]) {
      return;
    }

    const parentTasks = compact(
      t.timing.dependentOn.map((x) => x.itemType === 'TASK' && tsks[x.itemId])
    );
    const lastDate = Math.max(
      ...parentTasks.map((p) => p.timing.end.toMillis())
    );
    if (!lastDate || lastDate === -Infinity) {
      return;
    }
    const endTs = tsFromMillis(lastDate);

    const nextTask = createNotStartedTask(
      d.id,
      t,
      participant,
      {
        start: endTs,
        end: addToTs(endTs, t.timing.end.duration)
      },
      d.data.anchorDate.at
    );
    if (!nextTask) {
      return;
    }

    // Distinct id to make this easier to debug
    nextTask.id = `${t.id}-triggered`;

    const subTasksByTplId = keyBy(nextTask.subTasks, (s) => s.templateId);
    d.data.preSkips?.forEach((p) => {
      if (p.type === 'SUB_TASK') {
        const s = subTasksByTplId[p.id];
        if (s) {
          s.status = SubTaskStatus.SKIPPED;
        }
      }
    });
    if (ifTasksAndAllAreSkipped(parentTasks)) {
      nextTask.subTasks.forEach((s) => (s.status = SubTaskStatus.SKIPPED));
    }

    if (every(nextTask.subTasks, (s) => s.status === SubTaskStatus.SKIPPED)) {
      nextTask.status = TaskStatus.SKIPPED;
    }

    tsks[t.id] = nextTask;
    untriggeredItems.tasks[t.id] = {
      task: nextTask,
      triggeredBy: parentTasks.map((x) => x.templateId)
    };
  });

  getAllMessageTemplates(tpl.data.messages).forEach((m) => {
    if (m.timing.type === 'fixed') {
      return;
    }
    if (m.timing.type === 'dataChange') {
      return;
    }
    if (msgs[m.id]) {
      return;
    }

    const parentTasks = compact(
      m.timing.dependentOn.map((x) => x.itemType === 'TASK' && tsks[x.itemId])
    );
    const lastDate = Math.max(
      ...parentTasks.map((p) => p.timing.end.toMillis())
    );
    if (!lastDate || lastDate === -Infinity) {
      return;
    }
    const endTs = tsFromMillis(lastDate);

    const nextStatus = createNotStartedMessageStatus(
      m,
      participant,
      endTs,
      d.data.anchorDate.at
    );
    if (!nextStatus) {
      return;
    }

    if (
      ifTasksAndAllAreSkipped(parentTasks) ||
      !!d.data.preSkips?.find((p) => p.type === 'MSG' && p.id === m.id)
    ) {
      nextStatus.status = MessageStatus.SKIPPED;
    }

    msgs[m.id] = nextStatus;
    untriggeredItems.msgs[m.id] = {
      msg: nextStatus,
      triggeredBy: parentTasks.map((x) => x.templateId)
    };
  });

  tpl.data.automations.forEach((a) => {
    if (
      a.timing.type === 'fixed' ||
      a.timing.type === 'dataChange' ||
      !participant
    ) {
      return;
    }

    if (auts[a.id]) {
      return;
    }

    const parentTasks = compact(
      a.timing.dependentOn.map((x) => x.itemType === 'TASK' && tsks[x.itemId])
    );
    const lastDate = Math.max(
      ...parentTasks.map((p) => p.timing.end.toMillis())
    );
    if (!lastDate || lastDate === -Infinity) {
      return;
    }
    const endTs = tsFromMillis(lastDate);

    const nextAutomation = createUpcomingAutomationInstance(
      d.id,
      a,
      participant,
      d.data.anchorDate.at,
      endTs,
      {}
    );
    if (!nextAutomation) {
      return;
    }

    // Distinct id to make this easier to debug
    nextAutomation.id = `${a.id}-triggered`;

    // I think actions are already a dictionary keyed by templateId, but let's be explicit
    const actionsByTplId = keyBy(
      Object.values(nextAutomation.actions),
      (a) => a.templateId
    );
    d.data.preSkips?.forEach((p) => {
      if (p.type === 'AUTOMATION_ACTION') {
        const a = actionsByTplId[p.id];
        if (a) {
          a.status = AutomationActionStatus.SKIPPED;
        }
      }
    });
    if (ifTasksAndAllAreSkipped(parentTasks)) {
      Object.values(nextAutomation.actions).forEach(
        (a) => (a.status = AutomationActionStatus.SKIPPED)
      );
    }

    if (
      every(
        Object.values(nextAutomation.actions),
        (a) => a.status === AutomationActionStatus.SKIPPED
      )
    ) {
      nextAutomation.status = AutomationStatus.SKIPPED;
    }

    auts[a.id] = nextAutomation;
    untriggeredItems.auts[a.id] = {
      aut: nextAutomation,
      triggeredBy: parentTasks.map((x) => x.templateId)
    };
  });

  const enhancedD = updateDataInDoc(d, {
    tasks: Object.values(tsks),
    messagesStatuses: msgs,
    automations: auts
  });

  const tasks = toDenormalizedTasks(
    enhancedD,
    tpl,
    actors,
    '_____EVERYONE______',
    replacements,
    resources,
    interpolationOptions,
    { includeSkippedITems: true }
  );
  const automations = toDenormalizedAutomationsWithInterpolations(
    enhancedD,
    tpl,
    replacements,
    resources,
    interpolationOptions
  );
  const messages = toDenormalizedMessages(
    enhancedD,
    tpl,
    '_____EVERYONE______',
    actors[PARTICIPANT_ACTOR_KEY],
    replacements,
    interpolationOptions,
    { ignoreCurrentTime: true, includeSkippedItems: true }
  );

  const audiences = {
    tasks: Object.fromEntries(
      enhancedD.data.tasks.map((tsk) => [tsk.templateId, tsk.audience])
    ),
    automations: mapValues(enhancedD.data.automations, (a) => a.audience),
    messages: mapValues(enhancedD.data.messagesStatuses, (m) => m.audience)
  };

  const customizedTimings = {
    tasks: Object.fromEntries(
      enhancedD.data.tasks.map((tsk) => [
        tsk.templateId,
        tsk.timing.manualOverride
      ])
    ),
    automations: mapValues(
      enhancedD.data.automations,
      (a) => a.timing.manualOverride
    ),
    messages: mapValues(
      enhancedD.data.messagesStatuses,
      (m) => m.timing.manualOverride
    )
  };

  const items: ItemData[] = orderBy(
    [
      ...messages.map((data) => ({
        type: 'MESSAGE' as const,
        actor: actorDescriptors[data.actor] || null, // TODO replace with a sensible default
        ccActorKeys: (
          getMessageTemplateById(tpl, data.templateId)?.receivesCopy?.roles ||
          []
        ).map((r: any) => (typeof r === 'string' ? r : r.key)),
        audience: getMessageAudiencesFromTemplateById(
          tpl,
          data.templateId,
          audiences.messages[data.templateId]
        ),
        customizedTiming: customizedTimings.messages[data.templateId],
        channels: getMessageTemplateById(tpl, data.templateId)?.channels || [],
        data,
        triggeredBy: compact(
          untriggeredItems.msgs[data.templateId]?.triggeredBy.map((x) =>
            tasks.find((tsk) => tsk.templateId === x)
          )
        )
      })),
      ...tasks.map((data) => ({
        type: 'TASK' as const,
        actor: actorDescriptors[data.actor] || null, // TODO replace with a sensible default
        ccActorKeys: (
          getTaskTemplateById(tpl, data.templateId)?.receivesCopy?.roles || []
        ).map((r: any) => (typeof r === 'string' ? r : r.key)),
        audience: getTaskAudiencesFromTemplateById(
          tpl,
          data.templateId,
          audiences.tasks[data.templateId]
        ),
        customizedTiming: customizedTimings.tasks[data.templateId],
        channels: getTaskTemplateById(tpl, data.templateId)?.channels || [],
        data,
        triggeredBy: compact(
          untriggeredItems.tasks[data.templateId]?.triggeredBy.map((x) =>
            tasks.find((tsk) => tsk.templateId === x)
          )
        )
      })),
      ...automations.map((data) => ({
        type: 'AUTOMATION' as const,
        audience: getAutomationAudiencesFromTemplateById(
          tpl,
          data.templateId,
          audiences.automations[data.templateId]
        ),
        customizedTiming: customizedTimings.automations[data.templateId],
        data,
        triggeredBy: compact(
          untriggeredItems.auts[data.templateId]?.triggeredBy.map((x) =>
            tasks.find((tsk) => tsk.templateId === x)
          )
        )
      }))
    ],
    [
      (x) => {
        if (x.triggeredBy?.length) {
          const lastDate = Math.max(
            ...x.triggeredBy.map((p) => p.start.toMillis())
          );
          return lastDate; // this doesn't work super well with chaining. meh.
        }
        if (x.type === 'TASK') {
          return x.data.start.toMillis();
        }
        if (x.type === 'AUTOMATION') {
          if (x.data.timing.type === 'ABSOLUTE') {
            return x.data.timing.start.toMillis();
          }
          // TODO for those tied with a task, look up the task and its start date
          return Infinity;
        }
        if (x.type === 'MESSAGE') {
          return x.data.sendAt.toMillis();
        }
      },
      (x) => (x.triggeredBy?.length ? 1 : 0),
      (x) => getActorSortKey(x.type === 'AUTOMATION' ? null : x.data.actor),
      (x) => {
        return x.type === 'MESSAGE' ? 0 : x.type === 'TASK' ? 1 : 2;
      },
      (x) => {
        return x.type === 'MESSAGE'
          ? getAllMessageTemplates(tpl.data.messages).findIndex(
              (m) => m.id === x.data.templateId
            )
          : x.type === 'TASK'
          ? tpl.data.tasks.findIndex((t) => t.id === x.data.templateId)
          : x.type === 'AUTOMATION'
          ? tpl.data.automations.findIndex((a) => a.id === x.data.templateId)
          : Infinity;
      }
    ]
  );

  return {
    user: participant,
    // These are only used in the log for now and technically only need to include items that have already happened
    tasks: toDenormalizedTasks(
      d,
      tpl,
      actors,
      '_____EVERYONE______',
      replacements,
      resources,
      interpolationOptions,
      { includeSkippedITems: true }
    ),
    messages,
    automations: toDenormalizedAutomationsWithInterpolations(
      enhancedD,
      tpl,
      replacements,
      resources,
      interpolationOptions
    ),
    // These are used in the status list and contain synthetic items that e.g. might not have triggered yet
    items,
    // Exposed so timeline-item renderers can interpolate adminName / title
    // for tokens like {{ participant.fullName }} or {{ <supporting-actor>.* }}.
    // Previously each renderer built an empty `roles: []` replacements object
    // and only experience/org tokens resolved.
    replacements
  };
};
